import React from "react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import ChartTooltip from "../ChartTooltip"

// accepts chartData, xDataKey, yDataKey, xAxisLabel(used for month) as props to generate the simple bar chart.

export default function SimpleBarChart({
  chartData,
  xDataKey,
  yDataKey,
  xAxisLabel,
  color,
  hidePercent,
  tickCount,
  barSize,
  chartHeight,
  showEllipsis,
  marginRight,
  marginTop,
  xAxisLabelGap,
}) {
  function buildStrings(elements, maxLength = 14) {
    const result = []
    let current = ""

    for (const element of elements) {
      if (current.length + element.length > maxLength) {
        result.push(current)
        current = ""
      }
      current += " " + element
    }

    if (current !== "") {
      result.push(current)
    }

    return result
  }

  const CustomizedAxisTick = ({x, y, payload}) => {
    const input = payload.value
    let customTick = null
    if (showEllipsis) {
      customTick = (
        <g transform={`translate(${x},${y})`}>
          <text x={0} y={0} dy={0} textAnchor="end" fill="#666" transform="rotate(-45)">
            {input.length > 10 ? (
              <tspan>{`${input.substring(0, 10)}...`}</tspan>
            ) : (
              <tspan>{input}&nbsp;&nbsp;&nbsp;</tspan>
            )}
          </text>
        </g>
      )
    } else {
      customTick = (
        <g transform={`translate(${x},${y})`}>
          <text x={0} y={0} dy={0} textAnchor="end" fill="#666" transform="rotate(-45)">
            {
              input.includes(",") ? (
                <>
                  <tspan x="0" dy="1em">{`${input.split(",")[0]},`}</tspan>{" "}
                  <tspan x="0" dy="1em">
                    {input.split(",")[1]}
                  </tspan>
                </>
              ) : (
                buildStrings(input.split(" ")).map((val, index) => (
                  <tspan key={index} x="0" dy={index ? "1.1em" : "1em"}>
                    {val}
                  </tspan>
                ))
              ) //if brand name is too long then split up the brand name
            }
          </text>
        </g>
      )
    }
    return customTick
  }

  return (
    <ResponsiveContainer height={chartHeight ? chartHeight : 361} width="100%">
      <BarChart
        data={chartData}
        margin={{top: marginTop ? marginTop : 0, right: marginRight ? marginRight : 0}}
      >
        <CartesianGrid strokeDasharray="4 4" vertical={false} strokeOpacity="0.7" />
        <XAxis
          dataKey={xDataKey}
          stroke="#0A3F36"
          label={{
            value: xAxisLabel,
            style: {
              opacity: 1,
              fontWeight: 400,
              lineHeight: "160%",
              fontFamily: "Poppins",
            },
            dy: xAxisLabelGap ?? 80,
          }}
          strokeOpacity="1"
          interval={0}
          tickLine={false}
          tick={<CustomizedAxisTick />}
          tickFormatter={(val) => val.toLocaleString().replace(/,/g, " ")}
        />
        <YAxis
          interval={0}
          tickCount={tickCount ? tickCount : 5}
          axisLine={false}
          tickLine={false}
          tickFormatter={(tick) => (hidePercent ? `${tick}` : `${tick}%`)}
        />
        <Tooltip
          formatter={function (value, name) {
            return hidePercent ? `${value}` : `${value}%`
          }}
          labelFormatter={function (value) {
            return `${value}`
          }}
          xDataKey={xDataKey}
          content={<ChartTooltip />}
        />
        <Bar dataKey={yDataKey} fill={color} barSize={barSize ? barSize : ""} />
      </BarChart>
    </ResponsiveContainer>
  )
}
